import React from 'react';
import { Card, CardContent, Typography, Box, Link } from '@mui/material';

interface BreakdownItem {
  icon: string;
  label: string;
  value: number;
}

interface SummaryCardData {
  title: string;
  subtitle?: string;
  value: string | number;
  label?: string;
  breakdown?: BreakdownItem[];
  link?: string;
  onClick?: () => void;
}

interface SummaryCardProps {
  data: SummaryCardData;
  height?: number;
  onDetailsClick?: (data?: any) => void;
}

const SummaryCard: React.FC<SummaryCardProps> = ({ data, height, onDetailsClick }) => {
  const { title, subtitle, value, label, breakdown, link, onClick } = data;
  const handleClick = (item?: any) => {
    if (onDetailsClick) {
      onDetailsClick(item);
    }
  };

  return (
    <Card
      sx={{
        transition: 'all 0.3s ease-in-out',
        borderRadius: 5,
        height: height || 'auto',
        '&:hover': {
          boxShadow: 6,
          transform: 'scale(1.02)',
          cursor: onClick ? 'pointer' : 'default',
        },
      }}
    >
      <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Title Section */}
        <Box sx={{ mb: 3 }}>
          <Typography 
            variant="h6" 
            sx={{ 
              fontSize: '1rem',
              fontWeight: 600,
              color: 'text.secondary',
              letterSpacing: '0.5px'
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography 
              variant="body2" 
              sx={{ 
                fontSize: '0.875rem',
                color: 'text.secondary',
                mt: 0.5
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>

        {/* Main Value Section */}
        <Box sx={{ mb: 2 }}>
          {label && (
            <Typography 
              variant="body2" 
              sx={{ 
                fontSize: '0.875rem',
                color: 'text.secondary',
                mb: 0.5
              }}
            >
              {label}:
            </Typography>
          )}
          <Typography 
            variant="h2" 
            sx={{ 
              fontSize: '3rem',
              fontWeight: 700,
              color: 'primary.main'
            }}
          >
            {value}
          </Typography>
        </Box>

        {/* Breakdown Section */}
        {breakdown && breakdown.length > 0 && (
          <Box sx={{ mb: 2, flex: 1 }}>
            {breakdown.map((item, index) => (
              <Box 
                key={index}
                onClick={() =>handleClick(item)}
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  mb: 1.5,
                  '&:last-child': { mb: 0 }
                }}
              >
                <Typography sx={{ fontSize: '1.25rem', mr: 1.5 }}>
                  {item.icon}
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontSize: '0.95rem',
                    color: 'text.primary',
                    minWidth: '60px'
                  }}
                >
                  {item.label}:
                </Typography>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    ml: 1
                  }}
                >
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Link Section */}
        {link && (
          <Box sx={{ mt: 'auto', pt: 2 }}>
            <Link
              href="#"
              onClick={(e) => {
                e.preventDefault();
                onClick && onClick();
              }}
              sx={{
                fontSize: '0.875rem',
                color: 'primary.main',
                textDecoration: 'none',
                fontWeight: 500,
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              {link}
            </Link>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default SummaryCard;
